/**
 * Outbound List Component
 *
 * Displays list of outbound warehouse movements
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  CheckCircle,
  XCircle,
  Trash2,
  Pencil,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Truck,
  Download,
  FlaskConical,
  Snowflake
} from 'lucide-react';
import warehouseService from '../../services/warehouseService';
import TemplateSelector from '../TemplateSelector';
import excelExportService from '../../services/excelExportService';
import { formatBeijingDate } from '../../utils/dateUtils';
import FilterBar from './FilterBar';
import useDebounce from '../../hooks/useDebounce';
import useFilterPersistence from '../../hooks/useFilterPersistence';

// Calculate default date range (last 30 days)
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

function OutboundList({ refreshTrigger, canConfirm, onRefresh, productTypeFilter = 'all' }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Use filter persistence hook
  const [persistedState, setPersistedState] = useFilterPersistence('warehouse-outbound', {
    page: 0,
    rowsPerPage: 10,
    dateRange: getDefaultDateRange(),
    filters: {
      salesOrderName: '',
      clientName: '',
      sourceType: 'all'
    },
    includeFrozen: false
  });

  // Extract state from persisted state
  const page = persistedState.page;
  const rowsPerPage = persistedState.rowsPerPage;
  const dateRange = persistedState.dateRange;
  const filters = persistedState.filters;

  // Update functions
  const setPage = (newPage) => {
    setPersistedState(prev => ({
      ...prev,
      page: typeof newPage === 'function' ? newPage(prev.page) : newPage
    }));
  };

  const setRowsPerPage = (newRowsPerPage) => {
    setPersistedState(prev => ({
      ...prev,
      rowsPerPage: typeof newRowsPerPage === 'function' ? newRowsPerPage(prev.rowsPerPage) : newRowsPerPage
    }));
  };

  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  const setIncludeFrozen = (value) => {
    setPersistedState(prev => ({
      ...prev,
      includeFrozen: value
    }));
  };

  // Debounce filter values for performance (500ms delay)
  const debouncedSalesOrderName = useDebounce(filters.salesOrderName, 500);
  const debouncedClientName = useDebounce(filters.clientName, 500);

  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);

  // Load outbound movements
  const loadMovements = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        movement_type: 'outbound',
        page: page + 1,
        page_size: rowsPerPage
      };

      // Add date range filters - convert to UTC
      if (dateRange.startDate) {
        // Convert YYYY-MM-DD to UTC start of day (00:00:00 UTC)
        const startDate = new Date(dateRange.startDate + 'T00:00:00');
        params.date_from = startDate.toISOString();
      }
      if (dateRange.endDate) {
        // Convert YYYY-MM-DD to UTC end of day (23:59:59 UTC)
        const endDate = new Date(dateRange.endDate + 'T23:59:59');
        params.date_to = endDate.toISOString();
      }

      // Add sales order filter (using debounced value)
      if (debouncedSalesOrderName) {
        params.sales_order = debouncedSalesOrderName;
      }

      // Add client name filter (using debounced value)
      if (debouncedClientName) {
        params.client_name = debouncedClientName;
      }

      // Add product type filter
      if (productTypeFilter && productTypeFilter !== 'all') {
        params.product_type = productTypeFilter;
      }

      if (persistedState.includeFrozen) {
        params.include_frozen = 'true';
      }

      // Add source type filter
      if (filters.sourceType && filters.sourceType !== 'all') {
        params.source_type = filters.sourceType;
      }

      const response = await warehouseService.getMovements(params);
      setMovements(response.movements || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load outbound movements:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, dateRange, debouncedSalesOrderName, debouncedClientName, productTypeFilter, persistedState.includeFrozen, filters.sourceType]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements, refreshTrigger]);

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when date range changes
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      salesOrderName: '',
      clientName: '',
      sourceType: 'all'
    });
    setDateRange(getDefaultDateRange());
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleExpand = (movementId) => {
    setExpandedRows(prev => ({
      ...prev,
      [movementId]: !prev[movementId]
    }));
  };

  const handleConfirm = async (movement) => {
    if (!window.confirm('确认出库？这将扣减库存。')) {
      return;
    }

    try {
      await warehouseService.confirmMovement(movement._id);
      if (onRefresh) onRefresh();
      loadMovements();
    } catch (err) {
      alert(`确认失败: ${err.message}`);
    }
  };

  const handleCancel = async (movement) => {
    if (!window.confirm('确定要取消此出库单吗？')) {
      return;
    }

    try {
      await warehouseService.cancelMovement(movement._id);
      if (onRefresh) onRefresh();
      loadMovements();
    } catch (err) {
      alert(`取消失败: ${err.message}`);
    }
  };

  const handleDelete = async (movement) => {
    if (!window.confirm('确定要删除此出库单吗？此操作不可恢复。')) {
      return;
    }

    try {
      await warehouseService.deleteMovement(movement._id);
      if (onRefresh) onRefresh();
      loadMovements();
    } catch (err) {
      alert(`删除失败: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待确认', variant: 'outline' },
      confirmed: { label: '已确认', variant: 'default' },
      completed: { label: '已完成', variant: 'default' },
      cancelled: { label: '已取消', variant: 'destructive' }
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getReferenceBadge = (type) => {
    const typeConfig = {
      purchase_order: { label: '采购单', variant: 'secondary' },
      sales_order: { label: '销售单', variant: 'default' },
      material_requisition: { label: '领料单', variant: 'secondary' },
      manual: { label: '手动', variant: 'outline' },
      subcontract_order: { label: '外协发料', variant: 'secondary' }
    };

    const config = typeConfig[type] || { label: type, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleExportClick = (movement) => {
    setSelectedMovement(movement);
    setTemplateSelectorOpen(true);
  };

  const handleTemplateSelectorClose = () => {
    setTemplateSelectorOpen(false);
    setSelectedMovement(null);
  };

  const handleTemplateSelected = async (template) => {
    const movementToExport = selectedMovement;
    setTemplateSelectorOpen(false);
    setSelectedMovement(null);

    try {
      const templateId = template?.template_id;
      if (!templateId) {
        alert('未选择模板');
        return;
      }

      if (!movementToExport) {
        alert('未选择出库单');
        return;
      }

      const { blob, filename } = await excelExportService.exportUsingTemplate('warehouse_outbound', {
        template_id: templateId,
        movement_ids: [movementToExport._id],
      });

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(`导出失败: ${err.message}`);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <div>
      {/* Filter Bar */}
      <FilterBar
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        showSalesOrderFilter={true}
        showSourceTypeFilter={true}
        sourceTypeFilterMode="outbound"
      />

      <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={persistedState.includeFrozen}
            onChange={(e) => setIncludeFrozen(e.target.checked)}
            className="rounded border-gray-300"
          />
          <Snowflake className="h-4 w-4 text-blue-500" />
          <span className="text-sm text-muted-foreground">显示冻结库存</span>
        </label>
      </div>

      {/* Table */}
      <Card className="border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="border-b">
                <th className="p-3 w-12"></th>
                <th className="text-left p-3 font-medium">单号</th>
                <th className="text-left p-3 font-medium">状态</th>
                <th className="text-left p-3 font-medium">来源</th>
                <th className="text-left p-3 font-medium">客户</th>
                <th className="text-left p-3 font-medium">仓库</th>
                <th className="text-left p-3 font-medium">产品数</th>
                <th className="text-left p-3 font-medium">日期</th>
                <th className="text-right p-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center p-8">
                    <span className="text-muted-foreground">加载中...</span>
                  </td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center p-8">
                    <div className="flex flex-col items-center py-4">
                      <Truck className="h-12 w-12 text-muted-foreground mb-2" />
                      <span className="text-muted-foreground">暂无出库记录</span>
                    </div>
                  </td>
                </tr>
              ) : (
                movements.map((movement) => (
                  <Collapsible
                    key={movement._id}
                    open={expandedRows[movement._id]}
                    onOpenChange={() => handleToggleExpand(movement._id)}
                    asChild
                  >
                    <React.Fragment>
                      <tr className="border-b hover:bg-muted">
                        <td className="p-3">
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              {expandedRows[movement._id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {movement.source_reference || movement.movement_id || movement._id.slice(-6).toUpperCase()}
                            </span>
                            {movement.is_sample && (
                              <FlaskConical className="h-4 w-4 text-purple-500" title="样品出库" />
                            )}
                          </div>
                        </td>
                        <td className="p-3">{getStatusBadge(movement.status)}</td>
                        <td className="p-3">{getReferenceBadge(movement.source_type)}</td>
                        <td className="p-3">{movement.client_name || '-'}</td>
                        <td className="p-3">{movement.warehouse_location || 'main'}</td>
                        <td className="p-3">{movement.products?.length || 0}</td>
                        <td className="p-3">
                          {formatBeijingDate(movement.movement_date)}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {movement.status === 'pending' && (
                              <>
                                {canConfirm && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-success hover:text-success"
                                          onClick={() => handleConfirm(movement)}
                                        >
                                          <CheckCircle className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>确认出库</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                        onClick={() => handleCancel(movement)}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>取消</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-primary hover:text-primary"
                                    onClick={() => handleExportClick(movement)}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>导出</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {movement.status === 'pending' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // TODO: Implement edit functionality
                                      alert('编辑功能开发中');
                                    }}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    编辑
                                  </DropdownMenuItem>
                                )}
                                {movement.status !== 'completed' && (
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(movement)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    删除
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row - Product Details */}
                      <tr>
                        <td colSpan={9} className="p-0">
                          <CollapsibleContent>
                            <div className="p-4 bg-muted">
                              <h4 className="text-sm font-medium mb-2">产品明细</h4>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left p-2 font-medium">产品编码</th>
                                    <th className="text-left p-2 font-medium">产品名称</th>
                                    <th className="text-left p-2 font-medium">计划数量</th>
                                    <th className="text-left p-2 font-medium">实际数量</th>
                                    <th className="text-left p-2 font-medium">单位</th>
                                    <th className="text-left p-2 font-medium">库位</th>
                                    <th className="text-left p-2 font-medium">类型</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {movement.products?.map((product, index) => {
                                    // Generate unique key from product_id + specification + location + client + ref_number
                                    const lineItemKey = [
                                      product.product_id,
                                      product.specification || '',
                                      product.location || movement.warehouse_location || '',
                                      movement.client_ref || movement.client_name || '',
                                      product.ref_number || ''
                                    ].join('_') + `_${index}`;

                                    return (
                                      <tr key={lineItemKey} className="border-b">
                                        <td className="p-2">{product.product_id}</td>
                                        <td className="p-2">{product.name}</td>
                                        <td className="p-2">{product.quantity || 0}</td>
                                        <td className="p-2">{product.quantity_received || product.quantity || 0}</td>
                                        <td className="p-2">{product.unit}</td>
                                        <td className="p-2">{product.location || movement.warehouse_location}</td>
                                        <td className="p-2">
                                          {product.is_leftover ? (
                                            <span className="text-green-600 font-medium" title={`余料: ${product.leftover_code || ''}`}>
                                              余料
                                              {product.leftover_code && ` (${product.leftover_code})`}
                                            </span>
                                          ) : product.is_remaining ? (
                                            <span className="text-blue-600 font-medium">尾料</span>
                                          ) : product.category === '工具' || product.category === '成品' ? (
                                            <span className="text-gray-500">成品</span>
                                          ) : (
                                            <span className="text-gray-500">新料</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                              </table>

                              {movement.notes && (
                                <div className="mt-3">
                                  <span className="text-sm text-muted-foreground">
                                    备注: {movement.notes}
                                  </span>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </td>
                      </tr>
                    </React.Fragment>
                  </Collapsible>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 px-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">每页显示</span>
          <Select value={String(rowsPerPage)} onValueChange={(val) => handleChangeRowsPerPage({ target: { value: val } })}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, total)} / {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= totalPages - 1}
          >
            下一页
          </Button>
        </div>
      </div>

      {/* Template Selector */}
      <TemplateSelector
        open={templateSelectorOpen}
        onClose={handleTemplateSelectorClose}
        onSelect={handleTemplateSelected}
        entityType="warehouse_outbound"
        title="选择出库单导出模板"
        allowManagement={true}
      />
    </div>
  );
}

export default OutboundList;
